import { db } from "@/lib/firebase";
import { Vote } from "@/types/surfReport";
import {
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
  Timestamp,
  increment,
} from "firebase/firestore";

const VOTES_COLLECTION = "votes";
const LIKES_COLLECTION = "likes";
const REPORTS_COLLECTION = "reports";

function voteDocId(reportId: string, userId: string) {
  return `${reportId}_${userId}`;
}

/* ---------- Get user's vote for a report ---------- */

export async function getUserVote(
  reportId: string,
  userId: string,
): Promise<Vote | null> {
  const snap = await getDoc(doc(db, VOTES_COLLECTION, voteDocId(reportId, userId)));
  if (!snap.exists()) return null;
  return snap.data() as Vote;
}

/* ---------- Cast or change a vote ---------- */

export async function castVote(
  reportId: string,
  userId: string,
  voteType: "confirm" | "deny",
): Promise<void> {
  const docId = voteDocId(reportId, userId);
  const voteRef = doc(db, VOTES_COLLECTION, docId);
  const reportRef = doc(db, REPORTS_COLLECTION, reportId);

  await runTransaction(db, async (txn) => {
    const existing = await txn.get(voteRef);

    if (existing.exists()) {
      const prev = existing.data() as Vote;
      if (prev.voteType === voteType) {
        // Remove vote (toggle off)
        txn.delete(voteRef);
        const field = voteType === "confirm" ? "confirmCount" : "denyCount";
        txn.update(reportRef, { [field]: increment(-1) });
        return;
      }
      // Change vote direction
      txn.set(voteRef, {
        reportId,
        userId,
        voteType,
        createdAt: serverTimestamp() as Timestamp,
      });
      const incField = voteType === "confirm" ? "confirmCount" : "denyCount";
      const decField = voteType === "confirm" ? "denyCount" : "confirmCount";
      txn.update(reportRef, {
        [incField]: increment(1),
        [decField]: increment(-1),
      });
    } else {
      // New vote
      txn.set(voteRef, {
        reportId,
        userId,
        voteType,
        createdAt: serverTimestamp() as Timestamp,
      });
      const field = voteType === "confirm" ? "confirmCount" : "denyCount";
      txn.update(reportRef, { [field]: increment(1) });
    }
  });
}

/* ---------- Like / unlike ---------- */

function likeDocId(reportId: string, userId: string) {
  return `${reportId}_${userId}`;
}

export async function getUserLike(reportId: string, userId: string): Promise<boolean> {
  const snap = await getDoc(doc(db, LIKES_COLLECTION, likeDocId(reportId, userId)));
  return snap.exists();
}

export async function toggleLike(reportId: string, userId: string): Promise<boolean> {
  const docId = likeDocId(reportId, userId);
  const likeRef = doc(db, LIKES_COLLECTION, docId);
  const reportRef = doc(db, REPORTS_COLLECTION, reportId);

  return runTransaction(db, async (txn) => {
    const existing = await txn.get(likeRef);

    if (existing.exists()) {
      txn.delete(likeRef);
      txn.update(reportRef, { likeCount: increment(-1) });
      return false;
    } else {
      txn.set(likeRef, {
        reportId,
        userId,
        createdAt: serverTimestamp() as Timestamp,
      });
      txn.update(reportRef, { likeCount: increment(1) });
      return true;
    }
  });
}
